import React, { useEffect, useState, useRef } from "react";
import CustomButton from "@/common/ui/Button";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import { EditRole, AddRole, AssignedUsers, AssignUser, DeleteRole } from "./modal";
import RenderColumn from "@/components/employee/RenderColumn";
import { RoundLoader } from "@/common/ui/Loader";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { pageEventSelector } from "@/store/features/dashboardSlice";
import { useNavigate } from "react-router-dom";
import { useOrgRoles } from "@/services/role.services";
import { Button } from "@/components/ui/button";
import Icon, { Image } from "@/common/ui/Image";
import { PermissionDenied } from "@/common/ui/page/PermissionDeniedPage";
import { Center } from "@/common/css";
import { RxCross2 } from "react-icons/rx";
import { FiSearch } from "react-icons/fi";
import EmptyList from "@/components/common/EmptyList";
import { useAppDispatch } from "@/store/store";
import { setRolesCount, settingSelector } from "@/store/features/settingSlice";
import { isHasPermission } from "@/utils/helper/permissions";
import { PERMISSIONS } from "@/utils/constant/global.constant";
import { loginSelector } from "@/store/features/loginSlice";
import { toastErrorMessage } from "@/utils/helper/myToast";
import CommonTableView from "@/components/common/table/CommonTableView";

const columnsConfig = [
  {
    accessorKey: "roleName",
    header: "Role name",
  },
  {
    accessorKey: "noOfEmp",
    header: "No. of users",
  },
  {
    accessorKey: "createdBy",
    header: "Created by",
    cell: ({ row, column }: any) => <RenderColumn type={"profile"} row={row} column={column} />,
  },
  {
    accessorKey: "createdOn",
    header: "Created on",
    cell: ({ row, column }: any) => <RenderColumn type={"date-time"} row={row} column={column} />,
  },
  {
    accessorKey: "updatedBy",
    header: "Updated by",
    cell: ({ row, column }: any) => <RenderColumn type={"profile"} row={row} column={column} />,
  },
  {
    accessorKey: "lastUpdatedOn",
    header: "Last updated on",
    cell: ({ row, column }: any) => <RenderColumn type={"date-time"} row={row} column={column} />,
  },
];

const modalConfig: ModalConfig = {
  add: {
    component: AddRole,
    title: "Add role",
    type: "side",
    showCloseWarning: true,
  },
  edit: {
    component: EditRole,
    title: "Edit role",
    type: "side",
    showCloseWarning: true,
  },
  assignedUsers: {
    component: AssignedUsers,
    title: "Assigned users",
    type: "side",
    modalClassName: "!max-w-1/2 md:min-w-[650px]",
    showCloseWarning: true,
  },
  assignUser: {
    component: AssignUser,
    title: "Assign user",
    type: "side",
    showCloseWarning: true,
  },
  deleteRole: {
    component: DeleteRole,
    title: "Delete role",
    type: "center",
    showCloseWarning: true,
  },
};

interface RoleSearchProps {
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  refetch: () => void;
  navigate: any;
  openModal: any;
  isLoading: boolean;
  isFetching: boolean;
  roles: any;
}

const RoleSearch: React.FC<RoleSearchProps> = ({ searchQuery, setSearchQuery, refetch, navigate, openModal, roles }) => {
  const [localInput, setLocalInput] = React.useState(searchQuery);
  const { currentUser } = useSelector(loginSelector);

  const handleSearch = (val: string) => {
    setSearchQuery(val);
    setTimeout(() => refetch(), 200);
  };
  const handleAddRole = () => {
    if (isHasPermission(currentUser?.perms, PERMISSIONS.ROLES["Create Roles"])) {
      if (roles?.count >= 9) {
        return toastErrorMessage("You have reached the maximum role limit. To add a new role, please delete an existing one.");
      }
      openModal("add");
    }
  };
  return (
    <div className="flex gap-2">
      <div className="relative md:w-[250px] hidden md:flex">
        <input
          type="text"
          placeholder="Search"
          className="pl-3 pr-[54px] py-1.5 w-full rounded-md border border-gray-300 text-13 !leading-normal outline-none focus:ring-1 focus:ring-text-primary placeholder:text-gray-400"
          value={localInput}
          onChange={(e) => {
            const value = e.target.value;
            if (value.includes("%") || value.length > 50) return;
            if (value.length > 0 && value.trim().length === 0) return;
            setLocalInput(value);
          }}
        />
        <Center className="absolute right-3 top-1/2 -translate-y-1/2">
          {localInput?.length > 0 ? (
            <Center className="gap-1">
              <RxCross2
                className="size-5 text-text-default hover:cursor-pointer"
                onClick={() => {
                  setLocalInput("");
                  handleSearch("");
                }}
              />
              <Image src="/icons/search.svg" alt="" className=" size-5 hover:cursor-pointer" onClick={() => handleSearch(localInput)} />
            </Center>
          ) : (
            <FiSearch className=" text-text-primary" />
          )}
        </Center>
      </div>
      <CustomButton
        variant="outline"
        size="small"
        className="!text-10 md:!text-13 !text-text-primary !bg-primaryOpacity-10 !border-none"
        isLoading={false}
        disabled={!isHasPermission(currentUser?.perms, PERMISSIONS.ROLES["Manage Permission"])}
        onClick={() => navigate("/home/settings/roles/manage-permissions")}
      >
        Manage permissions
      </CustomButton>
      <div className="relative  group">
        <CustomButton
          variant="primary"
          size="small"
          className=" "
          baseClassName="!text-10 md:!text-13 !leading-normal font-medium whitespace-nowrap rounded"
          disabled={!isHasPermission(currentUser?.perms, PERMISSIONS.ROLES["Create Roles"])}
          onClick={handleAddRole}
        >
          Add role
        </CustomButton>
      </div>
    </div>
  );
};

const ShowCount: React.FC<{ loading: boolean }> = React.memo(() => {
  const { rolesCount } = useSelector(settingSelector);
  const lastValid = useRef<number | undefined>(rolesCount);
  if (rolesCount !== undefined) {
    lastValid.current = rolesCount;
  }
  // Show last valid count while loading
  return lastValid.current !== undefined ? (
    <span className="pl-1 pt-1 text-xs leading-normal font-semibold text-text-default">{` (${lastValid.current})`}</span>
  ) : null;
});

const Roles: React.FC<{ openModal: any }> = ({ openModal }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { data: roles, isLoading, refetch, isFetching } = useOrgRoles({ query: searchQuery });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const pageEvent = useSelector(pageEventSelector);
  const { currentUser } = useSelector(loginSelector);
  const dropdownOptions = [
    {
      label: "Edit role",
      show: (_row: any, _openModal: any, currentUser: any) => {
        return _row?.allowEdit && isHasPermission(currentUser?.perms, PERMISSIONS.ROLES["Edit Roles"]);
      },
      onClickFun: (row: any, openModal: any) => {
        openModal("edit", { role: row });
      },
    },
    {
      label: "Manage permissions",
      show: (_row: any, _openModal: any, currentUser: any) => isHasPermission(currentUser?.perms, PERMISSIONS.ROLES["Manage Permission"]),
      onClickFun: (_: any, navigate: any) => {
        navigate("/home/settings/roles/manage-permissions");
      },
    },
    {
      label: "Assigned users",
      onClickFun: (row: any, openModal: any) => openModal("assignedUsers", { role: row, forceTitle: `${row.roleName} (${row.noOfEmp})` }),
    },
    // {
    //   label: "Assign user",
    //   show: (row: any) => ["Account Owner", "Account Admin"].includes(row.roleName),
    //   onClickFun: (row: any, openModal: any) => openModal("assignUser", { role: row, forceTitle: `Assign user to ${row.roleName} role` }),
    // },
    {
      label: "Delete",
      type: "remove",
      show: (_row: any, _openModal: any, currentUser: any) => _row?.allowEdit && isHasPermission(currentUser?.perms, PERMISSIONS.ROLES["Delete Roles"]),
      onClickFun: (row: any, openModal: any) => openModal("deleteRole", { role: row }),
    },
  ];

  useEffect(() => {
    if (pageEvent?.keys.includes("role-list") && pageEvent?.type === "refresh") {
      refetch();
    }
  }, [pageEvent]);

  useEffect(() => {
    if (roles?.count) {
      dispatch(setRolesCount(roles.count));
    }
  }, [roles?.count]);

  if (!isHasPermission(currentUser?.perms, PERMISSIONS.ROLES["View Roles"])) {
    return <PermissionDenied />;
  }
  if (!isLoading && !isFetching && !roles?.data) {
    return <PermissionDenied />;
  }

  return (
    <div className="h-full">
      <div className="flex justify-between items-center pb-4 sticky top-0 z-30 bg-white p-4">
        <div className="flex items-center justify-center">
          <h2 className="text-md font-semibold text-text-default">Roles</h2>
          <ShowCount loading={isLoading || isFetching} />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (isLoading || isFetching) return;
              refetch();
            }}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <Icon src="/icons/refresh.svg" alt="refresh icon" className={`size-5 `} />
          </Button>
        </div>

        <RoleSearch
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          refetch={refetch}
          navigate={navigate}
          openModal={openModal}
          isLoading={isLoading}
          isFetching={isFetching}
          roles={roles}
        />
      </div>
      {isLoading || isFetching ? (
        <motion.div
          className="bg-white/70 backdrop-blur flex items-center justify-center z-10 rounded-lg h-[calc(100%-114px)]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <RoundLoader />
        </motion.div>
      ) : roles?.data.length === 0 && !isLoading ? (
        <Center className="min-h-[300px] h-[calc(100%-114px)]">
          <EmptyList text="Oops! no record found" icon="/icons/empty-inbox.svg" className="gap-4" />
        </Center>
      ) : (
        <CommonTableView
          className=""
          data={roles?.data || []}
          columnsConfig={columnsConfig}
          dropdownOptions={dropdownOptions.map((opt) => ({
            ...opt,
            onClickFun: (row: any) => (opt.label === "Manage permissions" ? opt.onClickFun(row, navigate) : opt.onClickFun(row, openModal)),
            show: (row: any) => (typeof opt.show === "function" ? opt.show(row, openModal, currentUser) : true),
          }))}
          hidePagination={true}
          setSelectedRows={() => {}}
          openModal={openModal}
          rowSelection={{}}
          setRowSelection={() => {}}
        />
      )}
    </div>
  );
};

const RolesPage = withModal(Roles, modalConfig);
export default RolesPage;
